import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Card, CardHeader, Input, Dialog, Bar } from "@ui5/webcomponents-react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ButtonContained from "../StyledComponents/ButtonContained";
import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import httpRequest from "../../httpRequest";

const PositionsCard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [positions, setPositions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-positions`);
      setPositions(response.data.positions);
    } catch (error) {
      enqueueSnackbar("Failed to fetch positions", { variant: "error" });
    }
  };

  const handleOpenEditDialog = (position) => {
    setSelectedPosition(position);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setSelectedPosition(null);
    setEditDialogOpen(false);
  };

  const handleUpdatePosition = async () => {
    try {
      await httpRequest.put(`${process.env.REACT_APP_BACKEND_URL}/update-position/${selectedPosition.id}`, {
        position_name: selectedPosition.position_name,
      });
      enqueueSnackbar("Position updated successfully", { variant: "success" });
      fetchPositions();
      handleCloseEditDialog();
    } catch (error) {
      enqueueSnackbar("Failed to update position", { variant: "error" });
    }
  };

  const handleOpenAddDialog = () => {
    setSelectedPosition({ position_name: "" });
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setSelectedPosition(null);
    setAddDialogOpen(false);
  };

  const handleAddPosition = async () => {
    try {
      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-position`, {
        position_name: selectedPosition.position_name,
      });
      enqueueSnackbar("Position added successfully", { variant: "success" });
      fetchPositions();
      handleCloseAddDialog();
    } catch (error) {
      enqueueSnackbar("Failed to add position", { variant: "error" });
    }
  };

  const handleDeleteFunction = async (positionId) => {
    setSelectedPosition({ id: positionId });
    setDeleteConfirmationOpen(true);
  };

  const cancelDelete = () => {
    setSelectedPosition(null);
    setDeleteConfirmationOpen(false);
  };

  const confirmDelete = async () => {
    try {
      await httpRequest.delete(`${process.env.REACT_APP_BACKEND_URL}/delete-position/${selectedPosition.id}`);
      enqueueSnackbar("Position deleted successfully", { variant: "success" });
      fetchPositions();
      cancelDelete();
    } catch (error) {
      enqueueSnackbar("Failed to delete position", { variant: "error" });
    }
  };

  return (
    <>
      <Card header={<CardHeader subtitleText="Create and manage positions" titleText="Position Management" />}>
        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <input
            type="text"
            placeholder="Search Positions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: "1", padding: "10px", marginRight: "20px" }}
          />
          <ButtonContained variant="contained" color="primary" onClick={handleOpenAddDialog}>
            <AddIcon /> Add New
          </ButtonContained>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto', minWidth: 600 }}>Position Name</TableCell>
              <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontWeight: "bold", width: 'auto' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positions.filter(position => position.position_name.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((position, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>{position.position_name}</TableCell>
                  <TableCell sx={{ fontFamily: "SapFiori72, sans-serif" }}>
                    <ButtonContained style={{ marginRight: 10 }} onClick={() => handleOpenEditDialog(position)}>
                      <EditIcon /> Edit
                    </ButtonContained>
                    <ButtonOutlined onClick={() => handleDeleteFunction(position.id)}>
                      <DeleteIcon /> Delete
                    </ButtonOutlined>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        headerText="Confirm Delete"
        open={deleteConfirmationOpen}
        onAfterClose={cancelDelete}
        state="Critical"
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={cancelDelete}>
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={confirmDelete}>
                Delete
              </ButtonContained>
            </>
          } />
        }
      >
        Are you sure you want to delete this position?
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog
        headerText={`Edit Position`}
        open={editDialogOpen}
        onAfterClose={handleCloseEditDialog}
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseEditDialog}>
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={handleUpdatePosition}>
                Update
              </ButtonContained>
            </>
          } />
        }
      >
        <Input
          value={selectedPosition?.position_name || ""}
          onInput={(e) => setSelectedPosition({ ...selectedPosition, position_name: e.target.value })}
          placeholder="Position Name"
        />
      </Dialog>

      {/* Add Position Dialog */}
      <Dialog
        headerText={`Add Position`}
        open={addDialogOpen}
        onAfterClose={handleCloseAddDialog}
        footer={
          <Bar design="Footer" endContent={
            <>
              <ButtonOutlined style={{ marginRight: 8 }} onClick={handleCloseAddDialog}>
                Cancel
              </ButtonOutlined>
              <ButtonContained onClick={handleAddPosition}>
                Add
              </ButtonContained>
            </>
          } />
        }
      >
        <Input
          value={selectedPosition?.position_name || ""}
          onInput={(e) => setSelectedPosition({ ...selectedPosition, position_name: e.target.value })}
          placeholder="Position Name"
        />
      </Dialog>
    </>
  );
};

export default PositionsCard;
